<template>
  <transition name="live-recharge-sheet">
    <div v-if="visible" class="live-recharge-layer" role="dialog" aria-modal="true" :aria-label="$t('center.recharge')" @click.self="requestClose">
      <section class="live-recharge-panel">
        <header>
          <div>
            <small>QUICK RECHARGE</small>
            <strong>{{ $t("center.recharge") }}</strong>
          </div>
          <button type="button" :aria-label="$t('liveRoom.closePanel')" :disabled="submitting" @click="requestClose"><i class="el-icon-close" /></button>
        </header>

        <div v-if="loading" class="live-recharge-state"><i class="el-icon-loading" />{{ $t("common.loading") }}</div>
        <div v-else-if="failed" class="live-recharge-state failed">
          <i class="el-icon-warning-outline" />
          <button type="button" @click="loadWallet">{{ $t("common.retry") }}</button>
        </div>
        <div v-else class="live-recharge-scroll">
          <article class="live-recharge-balance">
            <span><small>{{ $t("centerDetail.balance") }}</small><strong>{{ $n(displayBalance) }}</strong></span>
            <i class="el-icon-coin" />
          </article>

          <div class="live-recharge-rate">{{ $t("centerPayment.exchangeRate", { dollars: $n(wallet.exchange_rate.amount || 1), coins: $n(wallet.exchange_rate.coin_amount || 100) }) }}</div>

          <div class="live-recharge-tiers">
            <button
              v-for="tier in wallet.tiers"
              :key="tier.amount"
              type="button"
              :class="{ active: recharge.mode === 'tier' && recharge.amount === Number(tier.amount) }"
              :data-analytics="`live.recharge_tier.${tier.amount}`"
              @click="selectRechargeTier(tier)"
            >
              <strong>{{ $n(tier.coin_amount) }}</strong>
              <small>{{ $t("wallet.coins") }}</small>
              <em>{{ $currency(tier.amount, "USD") }}</em>
            </button>
          </div>

          <section v-if="wallet.custom_amount_enabled" class="live-recharge-custom">
            <label for="live-recharge-custom-amount">{{ $t("centerDetail.customAmount") }}</label>
            <div :class="{ active: recharge.mode === 'custom', invalid: recharge.mode === 'custom' && recharge.customAmount && !rechargeAmountValid }">
              <span>USD</span>
              <input
                id="live-recharge-custom-amount"
                v-model.trim="recharge.customAmount"
                type="text"
                inputmode="decimal"
                maxlength="8"
                :placeholder="$t('centerDetail.customAmountPlaceholder')"
                @focus="recharge.mode = 'custom'"
                @input="handleCustomAmountInput"
              >
            </div>
            <small>{{ $t("centerDetail.customAmountRange", { minimum: $n(customAmountMinimum), maximum: $n(customAmountMaximum) }) }}</small>
            <p><span>{{ $t("centerDetail.estimatedCoins") }}</span><strong>{{ $n(rechargeCoinAmount) }} {{ $t("wallet.coins") }}</strong></p>
          </section>

          <div v-if="availableChannels.length" class="live-recharge-channels">
            <button
              v-for="channel in availableChannels"
              :key="channel.code"
              type="button"
              :class="{ active: recharge.channel === channel.code }"
              :data-analytics="`live.recharge_channel.${channel.code}`"
              @click="selectChannel(channel.code)"
            >
              <payment-brand-logo :channel="channel.code" />
              <span>{{ $t(`centerPayment.${channel.code}`) }}</span>
              <em>{{ $t("centerDetail.available") }}</em>
            </button>
          </div>
          <p v-else class="live-recharge-warning">{{ $t("centerDetail.noPaymentChannels") }}</p>

          <div v-if="paymentQuote" class="live-recharge-quote" aria-live="polite">
            <span><small>{{ $t("centerDetail.priceBase") }}</small><strong>{{ $currency(paymentQuote.base_amount, paymentQuote.base_currency) }}</strong></span>
            <span v-if="Number(paymentQuote.fee_amount) > 0"><small>{{ $t("centerDetail.paymentFee") }}</small><strong>{{ $currency(paymentQuote.fee_amount, paymentQuote.currency) }}</strong></span>
            <span class="total"><small>{{ $t("centerDetail.paymentTotal") }}</small><strong>{{ $currency(paymentQuote.paid_amount, paymentQuote.currency) }}</strong></span>
          </div>
          <p v-else-if="quoteFailed" class="live-recharge-warning">{{ $t("centerDetail.quoteUnavailable") }}</p>
          <p v-if="wechatBrowserUnsupported" class="live-recharge-warning wechat"><i class="el-icon-warning-outline" />{{ $t("centerDetail.wechatBrowserGuide") }}</p>
          <div v-if="externalPaymentUrl" class="live-external-payment-fallback" role="alert">
            <p>{{ $t("centerDetail.externalPaymentBlocked") }}</p>
            <a :href="externalPaymentUrl" target="_blank" rel="noopener noreferrer" data-analytics="live.recharge_external_retry"><i class="el-icon-top-right" />{{ $t("centerDetail.openExternalPayment") }}</a>
          </div>

          <button v-if="!externalPaymentUrl" type="button" class="live-recharge-submit" data-analytics="live.recharge_create" :disabled="submitting || quoteLoading || !paymentQuote || !rechargeAmountValid || wechatBrowserUnsupported" @click="createOrder">
            <i v-if="submitting" class="el-icon-loading" />{{ $t(submitting ? "centerDetail.checkoutOpening" : "centerDetail.continuePayment") }}
          </button>
          <p class="live-recharge-note"><i class="el-icon-lock" />{{ $t("centerDetail.securePaymentNote") }}</p>
        </div>
      </section>
    </div>
  </transition>
</template>

<script>
import { createRechargeOrder, getCenterWallet, getRechargeQuote } from "../api/center"
import { closeExternalPaymentWindow, prepareExternalPaymentWindow, redirectToPayment } from "../utils/paymentCheckout"
import PaymentBrandLogo from "./PaymentBrandLogo"

export default {
  name: "LiveRechargeSheet",
  components: { PaymentBrandLogo },
  props: {
    visible: { type: Boolean, default: false },
    balance: { type: Number, default: 0 }
  },
  data() {
    return {
      loading: false,
      failed: false,
      submitting: false,
      quoteLoading: false,
      quoteFailed: false,
      paymentQuote: null,
      quoteTimer: null,
      quoteRequestId: 0,
      externalPaymentUrl: "",
      wallet: { wallet: {}, tiers: [], channels: [], exchange_rate: {}, custom_amount: {} },
      recharge: { amount: 10, customAmount: "", mode: "tier", channel: "" }
    }
  },
  computed: {
    displayBalance() {
      const serverBalance = this.wallet.wallet && this.wallet.wallet.balance
      return serverBalance === undefined ? Number(this.balance || 0) : Number(serverBalance || 0)
    },
    customAmountMinimum() { return Number((this.wallet.custom_amount || {}).minimum || 1) },
    customAmountMaximum() { return Number((this.wallet.custom_amount || {}).maximum || 10000) },
    rechargeAmountCents() {
      const text = this.recharge.mode === "custom" ? String(this.recharge.customAmount || "") : String(this.recharge.amount || "")
      const match = text.match(/^(0|[1-9]\d{0,4})(?:\.(\d{1,2}))?$/)
      if (!match) return 0
      return Number(match[1]) * 100 + Number(String(match[2] || "").padEnd(2, "0"))
    },
    rechargeCoinAmount() { return this.rechargeAmountCents },
    rechargeAmountValid() {
      return this.rechargeAmountCents >= Math.round(this.customAmountMinimum * 100)
        && this.rechargeAmountCents <= Math.round(this.customAmountMaximum * 100)
    },
    selectedRechargeAmount() { return (this.rechargeAmountCents / 100).toFixed(2) },
    availableChannels() { return (this.wallet.channels || []).filter(channel => channel.available && channel.configured) },
    wechatBrowserUnsupported() { return this.recharge.channel === "wechat" && typeof navigator !== "undefined" && /MicroMessenger/i.test(navigator.userAgent) }
  },
  watch: {
    visible(visible) {
      if (visible) this.loadWallet()
    }
  },
  beforeDestroy() {
    clearTimeout(this.quoteTimer)
  },
  methods: {
    async loadWallet() {
      this.loading = true
      this.failed = false
      try {
        const response = await getCenterWallet()
        this.wallet = response.data || this.wallet
        const firstTier = (this.wallet.tiers || [])[0]
        if (firstTier && !(this.wallet.tiers || []).some(tier => Number(tier.amount) === Number(this.recharge.amount))) {
          this.selectRechargeTier(firstTier)
        }
        if (!this.availableChannels.some(channel => channel.code === this.recharge.channel)) {
          const preferred = this.availableChannels[0]
          this.recharge.channel = preferred ? preferred.code : ""
        }
        this.scheduleQuote()
      } catch (_error) {
        this.failed = true
      } finally {
        this.loading = false
      }
    },
    selectRechargeTier(tier) {
      this.recharge.mode = "tier"
      this.recharge.amount = Number(tier.amount)
      this.scheduleQuote()
    },
    handleCustomAmountInput() {
      this.recharge.mode = "custom"
      this.recharge.customAmount = String(this.recharge.customAmount || "").replace(",", ".").replace(/[^\d.]/g, "")
      this.scheduleQuote()
    },
    selectChannel(channel) {
      this.recharge.channel = channel
      this.scheduleQuote()
    },
    scheduleQuote() {
      clearTimeout(this.quoteTimer)
      this.externalPaymentUrl = ""
      this.paymentQuote = null
      this.quoteFailed = false
      if (!this.recharge.channel || !this.rechargeAmountValid) return
      this.quoteTimer = setTimeout(this.loadQuote, 220)
    },
    async loadQuote() {
      const requestId = ++this.quoteRequestId
      this.quoteLoading = true
      this.quoteFailed = false
      try {
        const response = await getRechargeQuote({ channel: this.recharge.channel, amount: this.selectedRechargeAmount })
        if (requestId === this.quoteRequestId) this.paymentQuote = response.data.quote
      } catch (_error) {
        if (requestId === this.quoteRequestId) { this.paymentQuote = null; this.quoteFailed = true }
      } finally {
        if (requestId === this.quoteRequestId) this.quoteLoading = false
      }
    },
    requestNo() {
      const randomPart = typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID().replace(/-/g, "")
        : `${Date.now()}${Math.random().toString(36).slice(2, 10)}`
      return `LIVECHARGE${randomPart}`.slice(0, 64)
    },
    async createOrder() {
      if (this.submitting) return
      if (!this.recharge.channel) return this.$notice.warning(this.$t("centerDetail.selectPayment"))
      if (!this.rechargeAmountValid) {
        return this.$notice.warning(this.$t("centerDetail.invalidRechargeAmount", { minimum: this.$n(this.customAmountMinimum), maximum: this.$n(this.customAmountMaximum) }))
      }
      const paymentWindow = prepareExternalPaymentWindow()
      let externalCheckoutOpened = false
      this.externalPaymentUrl = ""
      this.submitting = true
      try {
        const response = await createRechargeOrder({
          amount: this.selectedRechargeAmount,
          channel: this.recharge.channel,
          client_request_no: this.requestNo()
        })
        const data = response.data || {}
        const checkout = redirectToPayment(data.action, data.order, paymentWindow)
        externalCheckoutOpened = checkout.opened
        if (!checkout.opened) this.externalPaymentUrl = checkout.url
      } finally {
        if (!externalCheckoutOpened) closeExternalPaymentWindow(paymentWindow)
        this.submitting = false
      }
    },
    requestClose() {
      if (!this.submitting) this.$emit("close")
    }
  }
}
</script>
